import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Clock, Target } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const POPULAR_TEMPLATES = [
  {
    title: "30-Day Meditation Journey",
    description: "Build a daily meditation practice with guided support",
    challenge_type: "meditation",
    duration_days: 30,
    goal_criteria: { required_days: 25, target: 1, description: "Meditate 25 out of 30 days" },
    difficulty_level: "beginner",
    category: "mindfulness",
  },
  {
    title: "Quality Sleep Challenge",
    description: "Achieve 8+ hours of sleep for better wellbeing",
    challenge_type: "sleep",
    duration_days: 21,
    goal_criteria: { required_days: 18, target: 8, description: "Sleep 8+ hours for 18 days" },
    difficulty_level: "medium",
    category: "recovery",
  },
  {
    title: "Daily Goals Sprint",
    description: "Complete 2+ personal goals every day",
    challenge_type: "goals",
    duration_days: 14,
    goal_criteria: { required_days: 12, target: 2, description: "Complete 2+ goals for 12 days" },
    difficulty_level: "medium",
    category: "productivity",
  },
  {
    title: "Nutrition Excellence",
    description: "Eat healthy, whole foods for optimal health",
    challenge_type: "nutrition",
    duration_days: 30,
    goal_criteria: { required_days: 25, target: 7, description: "Healthy eating score 7+ for 25 days" },
    difficulty_level: "advanced",
    category: "nutrition",
  },
  {
    title: "Breathwork Basics",
    description: "Practice breathwork daily for stress relief",
    challenge_type: "breathwork",
    duration_days: 7,
    goal_criteria: { required_days: 6, target: 1, description: "Practice breathwork 6 out of 7 days" },
    difficulty_level: "beginner",
    category: "mindfulness",
  },
];

export function ChallengeTemplates() {
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const createFromTemplateMutation = useMutation({
    mutationFn: async (template: typeof POPULAR_TEMPLATES[0]) => {
      if (!user?.id) throw new Error("Not authenticated");

      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + template.duration_days);

      // Create challenge
      const { data: challenge, error: challengeError } = await supabase
        .from('group_challenges')
        .insert({
          title: template.title,
          description: template.description,
          challenge_type: template.challenge_type,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          goal_criteria: template.goal_criteria,
          is_public: true,
          created_by: user.id,
        })
        .select()
        .single();

      if (challengeError) throw challengeError;

      // Join the challenge
      await supabase
        .from('challenge_participants')
        .insert({
          challenge_id: challenge.id,
          user_id: user.id,
          completion_status: 'active',
        });

      return challenge;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['browse-challenges'] });
      queryClient.invalidateQueries({ queryKey: ['active-challenges'] });
      toast.success("Challenge created and joined!");
    },
  });

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'text-secondary';
      case 'medium': return 'text-primary';
      case 'advanced': return 'text-accent';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Popular Challenge Templates
        </CardTitle>
        <CardDescription>
          Start a new challenge instantly with these proven templates
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          {POPULAR_TEMPLATES.map((template) => (
            <Card key={template.title} className="border-secondary/20">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-base">{template.title}</CardTitle>
                    <CardDescription className="text-xs mt-1">
                      {template.description}
                    </CardDescription>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={`capitalize ${getDifficultyColor(template.difficulty_level)}`}
                  >
                    {template.difficulty_level}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>{template.duration_days} days</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Target className="h-4 w-4 text-muted-foreground" />
                    <span>{template.goal_criteria.description}</span>
                  </div>
                </div>
                <Button
                  className="w-full"
                  onClick={() => createFromTemplateMutation.mutate(template)}
                  disabled={createFromTemplateMutation.isPending}
                >
                  Start This Challenge
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
